/**
 * #443 (FULL do dono, fala 18c71de1 de 20/10/2026: "qualquer tipo de alertas técnicos, operacionais [...] esse alerta tem
 * que chegar na orquestração") — TELEMETRIA DOS ALERTAS TÉCNICOS QUE O DONO VÊ. Cada aviso técnico mostrado na conversa do
 * dono (web, app Android e as telas nativas do app) vira um evento que o motor lê sem LLM. A tela grava o evento numa fila
 * local no mesmo instante em que o aviso aparece e reenvia pelo id até o servidor confirmar; o aviso mostrado sem rede chega
 * depois. `shownSeq` é um contador por fila (`stream`) de cada aparelho: buraco na sequência é aviso exibido que o motor não
 * recebeu (fila perdida ou estourada), a medida do PRD (exibidos × recebidos, piso 1 exibido sem recebido).
 * Aqui só lógica pura: catálogo fechado, normalização limitada e o resumo que o REST, o MCP e a sonda leem.
 * Contrato: `docs/owner-surface-alerts.md`.
 */
/** Catálogo fechado: tipo → onde e o que o dono vê. Tipo fora do catálogo é recusado na entrada. */
export const OWNER_SURFACE_ALERT_KINDS = {
    offline_header: 'Cabeçalho da conversa em "Offline · suas mensagens ficam no aparelho."',
    pending_stuck: 'Fala do dono presa no aparelho há mais de 1 min ("ainda não saiu do aparelho")',
    delivery_attention: 'Fala do dono que ainda não chegou ao orquestrador ("ainda não chegou ao orquestrador")',
    push_permission_blocked: 'Notificações bloqueadas no Android',
    push_channel_blocked: 'Canal de alertas bloqueado no Android',
    push_reconnecting: 'Alertas reconectando há mais de 1 min',
    storage_error: 'Armazenamento do aparelho não abriu',
    history_error: 'Mensagens antigas não carregaram',
    draft_error: 'Rascunho não foi guardado',
    quote_error: 'Mensagem citada não abriu',
    alerts_error: 'Alteração dos alertas falhou',
    send_error: 'Envio da mensagem aguardando confirmação',
    reaction_error: 'Joinha não registrado',
    image_upload_error: 'Imagem não subiu',
    image_attach_error: 'Imagem não pôde ser anexada',
    audio_send_error: 'Envio do áudio não iniciou',
    voice_error: 'Gravação de áudio com erro',
    audio_interrupted: 'Áudio do dono interrompido no aparelho ("Áudio interrompido · cópia guardada")',
    audio_processing_pending: 'Áudio recebido com transcrição ou entrega pendente no servidor',
    audio_play_error: 'Áudio original não tocou ("Não foi possível ouvir")',
    succession_error: 'Confirmação da sucessão do orquestrador falhou',
    succession_blocked: 'Sucessão do orquestrador impedida por bloqueio técnico',
    management_unread: 'Gestão sem leitura atual de alguma fonte',
    app_update_error: 'Atualização do app Android falhou',
    native_offline_page: 'App Android na página "Sem conexão com o term-deck"',
    native_load_slow: 'App Android em "O Lenovo está demorando para responder"',
};
export const OWNER_SURFACE_ALERT_KIND_LIST = Object.keys(OWNER_SURFACE_ALERT_KINDS);
export function isOwnerSurfaceAlertKind(value) {
    return typeof value === 'string' && Object.prototype.hasOwnProperty.call(OWNER_SURFACE_ALERT_KINDS, value);
}
/** `web`: navegador; `android`: a mesma página dentro do app; `android_native`: tela nativa do app (vem do diagnóstico). */
export const OWNER_SURFACE_ALERT_SURFACES = ['web', 'android', 'android_native'];
export function ownerSurfaceAudioReference(value) {
    if (!value || typeof value !== 'object' || !('recordingId' in value) || typeof value.recordingId !== 'string'
        || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.recordingId))
        return undefined;
    return { recordingId: value.recordingId.toLowerCase() };
}
export const OWNER_SURFACE_ALERT_ID = /^[A-Za-z0-9_-]{8,64}$/;
const MAX_TEXT = 300;
function stripControl(text) {
    let out = '';
    for (const ch of text)
        out += ch.charCodeAt(0) < 32 ? ' ' : ch;
    return out;
}
function clip(value, max) {
    if (typeof value !== 'string')
        return undefined;
    const t = stripControl(value).replace(/\s+/g, ' ').trim();
    if (!t)
        return undefined;
    return t.length > max ? `${t.slice(0, max)}…` : t;
}
/** Normaliza um evento aceito pelo schema: limites, texto sem controle, caminho sem query e o atraso de recebimento. */
export function normalizeOwnerSurfaceAlert(input, nowIso) {
    const text = clip(input.text, MAX_TEXT);
    const path = clip(input.path?.split(/[?#]/)[0], 120);
    const appVersion = clip(input.appVersion, 40);
    const audio = input.kind.startsWith('audio_') ? ownerSurfaceAudioReference(input.audio) : undefined;
    const shownAt = Date.parse(input.atIso), receivedAt = Date.parse(nowIso);
    return {
        id: input.id,
        episodeId: input.episodeId,
        phase: input.phase,
        kind: input.kind,
        surface: input.surface,
        conversation: 'donizeti',
        deviceId: String(input.deviceId).slice(0, 128),
        stream: String(input.stream).slice(0, 64),
        ...(input.phase === 'shown' && Number.isSafeInteger(input.shownSeq) && input.shownSeq > 0 ? { shownSeq: input.shownSeq } : {}),
        atIso: input.atIso,
        ...(text ? { text } : {}),
        ...(appVersion ? { appVersion } : {}),
        ...(audio ? { audio } : {}),
        ...(path ? { path } : {}),
        ...(input.phase === 'cleared' && input.clearedBy ? { clearedBy: input.clearedBy } : {}),
        receivedAtIso: nowIso,
        lagMs: Number.isFinite(shownAt) && Number.isFinite(receivedAt) ? Math.max(0, receivedAt - shownAt) : 0,
    };
}
/** Diagnóstico nativo do app que é aviso ao dono (tela nativa), no formato da telemetria. */
const NATIVE_KINDS = {
    app_offline_page: { kind: 'native_offline_page', text: 'Sem conexão com o term-deck' },
    app_load_slow: { kind: 'native_load_slow', text: 'O Lenovo está demorando para responder. Continuo tentando…' },
};
export function nativeDiagnosticAsOwnerAlert(diag) {
    const native = NATIVE_KINDS[diag.kind];
    if (!native)
        return null;
    const shownAt = Date.parse(diag.atIso), receivedAt = Date.parse(diag.receivedAtIso);
    return {
        id: `nat-${diag.id}`.slice(0, 64), episodeId: `nat-${diag.id}`.slice(0, 64), phase: 'shown', kind: native.kind, surface: 'android_native',
        conversation: 'donizeti', deviceId: diag.device?.model ? `android:${diag.device.model}`.slice(0, 128) : 'android', stream: 'android-diagnostics',
        atIso: diag.atIso, text: native.text, ...(diag.appVersion ? { appVersion: diag.appVersion } : {}), receivedAtIso: diag.receivedAtIso,
        lagMs: Number.isFinite(shownAt) && Number.isFinite(receivedAt) ? Math.max(0, receivedAt - shownAt) : 0,
    };
}
/**
 * cd0ee1db (23/10/2026) — tipos que podem ter mais de um aviso aberto na mesma página (um por áudio). Nos demais, cada
 * tipo tem uma posição só na tela, então um `shown` mais novo do mesmo tipo na mesma fila prova que o anterior saiu.
 */
const MULTI_INSTANCE_KINDS = new Set(['audio_interrupted', 'audio_processing_pending', 'audio_play_error']);
/** Aparelho sem nenhum evento há mais que isso, com aviso aberto, entra em `openSilent`. */
export const OWNER_SURFACE_ALERT_SILENT_MS = 30 * 60_000;
function percentile(sorted, p) {
    if (!sorted.length)
        return 0;
    return sorted[Math.min(sorted.length - 1, Math.max(0, Math.ceil((p / 100) * sorted.length) - 1))];
}
/**
 * Resumo para o motor. Contagens e atraso olham a janela (`sinceIso`, pelo recebimento); buracos e avisos abertos olham
 * tudo o que está guardado, porque são estado. Sem poda, a sequência de cada fila começa em 1, então faltar o 1 também
 * conta como exibido sem recebido.
 */
export function summarizeOwnerSurfaceAlerts(events, opts) {
    const now = Date.parse(opts.nowIso);
    const windowed = opts.sinceIso ? events.filter((e) => e.receivedAtIso >= opts.sinceIso) : events;
    const byKind = {};
    const bySurface = {};
    const lags = [];
    let shown = 0, cleared = 0, lastShown = null, lastReceived = null;
    for (const e of windowed) {
        if (!lastReceived || e.receivedAtIso > lastReceived)
            lastReceived = e.receivedAtIso;
        if (e.phase === 'cleared') {
            cleared++;
            continue;
        }
        shown++;
        byKind[e.kind] = (byKind[e.kind] ?? 0) + 1;
        bySurface[e.surface] = (bySurface[e.surface] ?? 0) + 1;
        lags.push(e.lagMs);
        if (!lastShown || e.atIso > lastShown)
            lastShown = e.atIso;
    }
    lags.sort((a, b) => a - b);
    const streams = new Map();
    const episodes = new Map();
    const closed = new Set();
    const deviceSignal = new Map();
    for (const e of events) {
        const seen = deviceSignal.get(e.deviceId);
        if (!seen || e.receivedAtIso > seen)
            deviceSignal.set(e.deviceId, e.receivedAtIso);
        if (e.phase === 'cleared') {
            closed.add(e.episodeId);
            continue;
        }
        if (e.surface !== 'android_native' && !episodes.has(e.episodeId))
            episodes.set(e.episodeId, e);
        if (e.shownSeq === undefined)
            continue;
        const key = `${e.deviceId}\u0000${e.stream}`;
        const s = streams.get(key) ?? { deviceId: e.deviceId, stream: e.stream, seqs: new Set(), lastReceivedAtIso: e.receivedAtIso };
        s.seqs.add(e.shownSeq);
        if (e.receivedAtIso > s.lastReceivedAtIso)
            s.lastReceivedAtIso = e.receivedAtIso;
        streams.set(key, s);
    }
    const gaps = [];
    for (const s of streams.values()) {
        const seqs = [...s.seqs];
        const first = Math.min(...seqs), last = Math.max(...seqs);
        const from = opts.trimmed ? first : 1;
        const missing = last - from + 1 - seqs.length;
        if (missing > 0)
            gaps.push({ deviceId: s.deviceId, stream: s.stream, missing, firstSeq: first, lastSeq: last, lastReceivedAtIso: s.lastReceivedAtIso });
    }
    gaps.sort((a, b) => b.missing - a.missing || a.deviceId.localeCompare(b.deviceId));
    // O mais novo de cada tipo, por fila: o que veio antes dele sem retirada foi substituído.
    const latestShown = new Map();
    for (const e of episodes.values()) {
        if (MULTI_INSTANCE_KINDS.has(e.kind))
            continue;
        const key = `${e.deviceId}\u0000${e.stream}\u0000${e.kind}`;
        const at = latestShown.get(key);
        if (!at || e.atIso > at)
            latestShown.set(key, e.atIso);
    }
    let supersededWithoutClear = 0;
    const openEvents = [...episodes.values()].filter((e) => {
        if (closed.has(e.episodeId))
            return false;
        if (!MULTI_INSTANCE_KINDS.has(e.kind) && latestShown.get(`${e.deviceId}\u0000${e.stream}\u0000${e.kind}`) > e.atIso) {
            supersededWithoutClear++;
            return false;
        }
        return true;
    }).sort((a, b) => a.atIso.localeCompare(b.atIso));
    const silentAt = (deviceId) => {
        const at = deviceSignal.get(deviceId);
        return { deviceLastSignalAtIso: at, deviceSilent: Number.isFinite(now) && now - Date.parse(at) > OWNER_SURFACE_ALERT_SILENT_MS };
    };
    const open = openEvents.slice(0, 50).map((e) => ({
        episodeId: e.episodeId, kind: e.kind, surface: e.surface, deviceId: e.deviceId, shownAtIso: e.atIso,
        ageMs: Number.isFinite(now) ? Math.max(0, now - Date.parse(e.atIso)) : 0, ...(e.text ? { text: e.text } : {}),
        ...silentAt(e.deviceId),
    }));
    return {
        schemaVersion: 1,
        received: windowed.length,
        shown,
        cleared,
        shownNotReceived: gaps.reduce((n, g) => n + g.missing, 0),
        gaps,
        open,
        openTotal: openEvents.length,
        openSilent: openEvents.filter((e) => silentAt(e.deviceId).deviceSilent).length,
        supersededWithoutClear,
        byKind,
        bySurface,
        lastShownAtIso: lastShown,
        lastReceivedAtIso: lastReceived,
        lagMs: lags.length ? { max: lags[lags.length - 1], p50: percentile(lags, 50), p95: percentile(lags, 95) } : null,
        trimmed: !!opts.trimmed,
    };
}
//# sourceMappingURL=owner-surface-alerts.js.map